import { useEffect, useMemo } from "react";
import { Globe2, Info } from "lucide-react";

import {
  DocumentUpload,
  Field,
  FieldError,
  SearchSelect,
} from "@/components/doctor/onboarding/fields";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { SUPPORTED_COUNTRIES } from "@/data/country";
import { GENDERS, LANGUAGE_OPTIONS, SPECIALIZATIONS, registrationYears } from "@/data/medical";
import { useOnboarding } from "@/hooks/doctor/use-onboarding";
import { citiesForRegion, regionNames } from "@/services/location/country-data";
import { analysePhone } from "@/services/location/phone-country";
import { cn } from "@/lib/utils";
import type { EstablishmentRole, PracticeOwnership, SupportedCountry } from "@/types/onboarding";

export function Hint({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-start gap-2 rounded-xl border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
      <Info className="mt-0.5 size-3.5 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

export function BasicsStep() {
  const { draft, update, errors, countryData, countryLoading } = useOnboarding();

  const countryNames = SUPPORTED_COUNTRIES.map(
    (country) => `${country.name} (${country.dialCode})`,
  );
  const selectedCountry = SUPPORTED_COUNTRIES.find((country) => country.iso === draft.countryIso);

  // Country detection from the typed number — the number wins over the picker.
  useEffect(() => {
    const analysis = analysePhone(draft.phone);
    if (analysis.supported && analysis.country && analysis.country !== draft.countryIso) {
      update({ countryIso: analysis.country as SupportedCountry, region: "", city: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.phone]);

  const regions = countryData ? regionNames(countryData) : [];
  const cities = countryData ? citiesForRegion(countryData, draft.region) : [];

  return (
    <div className="space-y-5">
      <Field label="Full name" htmlFor="fullName" required error={errors.fullName}>
        <Input
          id="fullName"
          value={draft.fullName}
          onChange={(event) => update({ fullName: event.target.value })}
          placeholder="Dr. Aditi Sharma"
          className="h-11"
        />
      </Field>

      <div className="space-y-2">
        <Label>
          Gender<span className="ml-0.5 text-destructive">*</span>
        </Label>
        <RadioGroup
          value={draft.gender}
          onValueChange={(gender) => update({ gender })}
          className="grid gap-2 sm:grid-cols-4"
        >
          {GENDERS.map((gender) => (
            <Label
              key={gender}
              htmlFor={`gender-${gender}`}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-xl border border-border px-3 py-3 text-sm transition-colors hover:bg-muted/60",
                draft.gender === gender && "border-primary bg-primary/5",
              )}
            >
              <RadioGroupItem id={`gender-${gender}`} value={gender} />
              {gender}
            </Label>
          ))}
        </RadioGroup>
        <FieldError message={errors.gender} />
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label="Country"
          required
          error={errors.countryIso}
          hint="Drives states, cities, colleges and councils."
        >
          <SearchSelect
            value={selectedCountry ? `${selectedCountry.name} (${selectedCountry.dialCode})` : ""}
            options={countryNames}
            placeholder="Select country"
            onChange={(label) => {
              const match = SUPPORTED_COUNTRIES.find((country) => label.startsWith(country.name));
              if (match)
                update({
                  countryIso: match.iso,
                  region: "",
                  city: "",
                  institute: "",
                  registrationCouncil: "",
                });
            }}
          />
        </Field>

        <Field
          label="Mobile number"
          htmlFor="phone"
          required
          error={errors.phone}
          hint={
            selectedCountry
              ? `Country code ${selectedCountry.dialCode}`
              : "Type the number with its country code."
          }
        >
          <Input
            id="phone"
            type="tel"
            inputMode="tel"
            value={draft.phone}
            onChange={(event) => update({ phone: event.target.value })}
            placeholder={
              selectedCountry ? `${selectedCountry.dialCode} 98765 43210` : "+91 98765 43210"
            }
            className="h-11"
          />
        </Field>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label={countryData?.meta.regionLabel ?? "State / region"}
          required
          error={errors.region}
        >
          <SearchSelect
            value={draft.region}
            options={regions}
            loading={countryLoading}
            disabled={!draft.countryIso}
            placeholder={draft.countryIso ? "Select" : "Select a country first"}
            searchPlaceholder="Search regions…"
            onChange={(region) => update({ region, city: "" })}
          />
        </Field>
        <Field label="City" required error={errors.city}>
          <SearchSelect
            value={draft.city}
            options={cities}
            loading={countryLoading}
            disabled={!draft.countryIso}
            allowCustom
            placeholder={draft.countryIso ? "Select or type your city" : "Select a country first"}
            searchPlaceholder="Search cities…"
            onChange={(city) => update({ city })}
          />
        </Field>
      </div>

      <Hint>
        <span className="inline-flex items-center gap-1">
          <Globe2 className="size-3" /> Lists load only for the country you choose, so nothing
          irrelevant is downloaded.
        </span>
      </Hint>
    </div>
  );
}

export function SpecializationStep() {
  const { draft, update, errors } = useOnboarding();
  return (
    <div className="space-y-5">
      <Field
        label="Primary specialization"
        required
        error={errors.specialization}
        hint="This is the specialization patients search by."
      >
        <SearchSelect
          value={draft.specialization}
          options={[...SPECIALIZATIONS]}
          allowCustom
          placeholder="Select your specialization"
          searchPlaceholder="Search specializations…"
          onChange={(specialization) => update({ specialization })}
        />
      </Field>

      <div className="space-y-2">
        <Label>Languages you consult in</Label>
        <div className="flex flex-wrap gap-2">
          {LANGUAGE_OPTIONS.map((language) => {
            const active = draft.languages.includes(language);
            return (
              <button
                key={language}
                type="button"
                onClick={() =>
                  update({
                    languages: active
                      ? draft.languages.filter((item) => item !== language)
                      : [...draft.languages, language],
                  })
                }
                className={cn(
                  "rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors",
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-muted",
                )}
              >
                {language}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function EducationStep() {
  const { draft, update, errors, countryData, countryLoading } = useOnboarding();
  const years = useMemo(() => registrationYears().map(String), []);

  return (
    <div className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Degree" required error={errors.degree}>
          <SearchSelect
            value={draft.degree}
            options={countryData?.degrees ?? []}
            loading={countryLoading}
            allowCustom
            disabled={!draft.countryIso}
            placeholder={draft.countryIso ? "Select your degree" : "Select a country first"}
            onChange={(degree) => update({ degree })}
          />
        </Field>
        <Field label="Year of completion" required error={errors.completionYear}>
          <SearchSelect
            value={draft.completionYear}
            options={years}
            placeholder="Select year"
            searchPlaceholder="Search years…"
            onChange={(completionYear) => update({ completionYear })}
          />
        </Field>
      </div>

      <Field
        label="College / institute"
        required
        error={errors.institute}
        hint="Search the recognised list, or type your institute if it isn't listed."
      >
        <SearchSelect
          value={draft.institute}
          options={countryData?.institutes ?? []}
          loading={countryLoading}
          allowCustom
          disabled={!draft.countryIso}
          placeholder={draft.countryIso ? "Search your college" : "Select a country first"}
          searchPlaceholder="Search colleges…"
          onChange={(institute) => update({ institute })}
        />
      </Field>

      <Field label="Years of experience" htmlFor="experience" required error={errors.experience}>
        <Input
          id="experience"
          type="number"
          min={0}
          max={70}
          value={draft.experience}
          onChange={(event) => update({ experience: event.target.value })}
          placeholder="12"
          className="h-11"
        />
      </Field>
    </div>
  );
}

const OWNERSHIP: { value: PracticeOwnership; title: string; description: string }[] = [
  {
    value: "own",
    title: "I own an establishment",
    description: "You run your own clinic, practice or hospital.",
  },
  {
    value: "visit",
    title: "I visit an establishment",
    description: "You consult at a clinic or hospital owned by someone else.",
  },
];

export function PracticeStep() {
  const { draft, update, errors } = useOnboarding();
  return (
    <div className="space-y-4">
      <RadioGroup
        value={draft.ownership}
        onValueChange={(ownership) => update({ ownership: ownership as PracticeOwnership })}
        className="grid gap-3"
      >
        {OWNERSHIP.map((option) => (
          <Label
            key={option.value}
            htmlFor={`ownership-${option.value}`}
            className={cn(
              "flex cursor-pointer items-start gap-3 rounded-xl border border-border p-4 transition-colors hover:bg-muted/60",
              draft.ownership === option.value && "border-primary bg-primary/5",
            )}
          >
            <RadioGroupItem
              id={`ownership-${option.value}`}
              value={option.value}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-medium">{option.title}</span>
              <span className="block text-xs text-muted-foreground">{option.description}</span>
            </span>
          </Label>
        ))}
      </RadioGroup>
      <FieldError message={errors.ownership} />
    </div>
  );
}

export function EstablishmentStep() {
  const { draft, update, errors, countryData, countryLoading } = useOnboarding();
  const cities = countryData ? citiesForRegion(countryData, draft.region) : [];

  return (
    <div className="space-y-5">
      <Field
        label="Establishment name"
        htmlFor="establishmentName"
        required
        error={errors.establishmentName}
      >
        <Input
          id="establishmentName"
          value={draft.establishmentName}
          onChange={(event) => update({ establishmentName: event.target.value })}
          placeholder="HealNova Care Centre"
          className="h-11"
        />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="City" required error={errors.establishmentCity}>
          <SearchSelect
            value={draft.establishmentCity}
            options={cities}
            loading={countryLoading}
            allowCustom
            placeholder="Select city"
            searchPlaceholder="Search cities…"
            onChange={(establishmentCity) => update({ establishmentCity })}
          />
        </Field>
        <Field label="Locality" htmlFor="locality" required error={errors.locality}>
          <Input
            id="locality"
            value={draft.locality}
            onChange={(event) => update({ locality: event.target.value })}
            placeholder="Indiranagar"
            className="h-11"
          />
        </Field>
      </div>
    </div>
  );
}

export function RegistrationStep() {
  const { draft, update, errors, countryData, countryLoading } = useOnboarding();
  const years = useMemo(() => registrationYears().map(String), []);

  return (
    <div className="space-y-5">
      <Field
        label="Registration number"
        htmlFor="registrationNumber"
        required
        error={errors.registrationNumber}
      >
        <Input
          id="registrationNumber"
          value={draft.registrationNumber}
          onChange={(event) => update({ registrationNumber: event.target.value })}
          placeholder="e.g. KMC/12345/2015"
          className="h-11"
        />
      </Field>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          label={countryData?.meta.councilLabel ?? "Registration council"}
          required
          error={errors.registrationCouncil}
        >
          <SearchSelect
            value={draft.registrationCouncil}
            options={countryData?.councils ?? []}
            loading={countryLoading}
            allowCustom
            disabled={!draft.countryIso}
            placeholder={draft.countryIso ? "Search councils" : "Select a country first"}
            searchPlaceholder="Search councils…"
            onChange={(registrationCouncil) => update({ registrationCouncil })}
          />
        </Field>
        <Field
          label="Registration year"
          required
          error={errors.registrationYear}
          hint="From 1926 to the current year."
        >
          <SearchSelect
            value={draft.registrationYear}
            options={years}
            placeholder="Select year"
            searchPlaceholder="Search years…"
            onChange={(registrationYear) => update({ registrationYear })}
          />
        </Field>
      </div>
    </div>
  );
}

export function ConsultationStep() {
  const { draft, update, errors, countryData } = useOnboarding();
  const symbol = countryData?.meta.currencySymbol ?? "₹";

  return (
    <div className="space-y-5">
      <Field label={`Consultation fee (${symbol})`} htmlFor="fees" required error={errors.fees}>
        <Input
          id="fees"
          type="number"
          min={0}
          value={draft.fees}
          onChange={(event) => update({ fees: event.target.value })}
          placeholder="800"
          className="h-11"
        />
      </Field>

      <div className="flex items-center justify-between rounded-xl border border-border p-4">
        <div>
          <p className="text-sm font-medium">Video consultations</p>
          <p className="text-xs text-muted-foreground">Let patients book online appointments.</p>
        </div>
        <Switch
          checked={draft.videoConsultationAvailable}
          onCheckedChange={(videoConsultationAvailable) => update({ videoConsultationAvailable })}
          aria-label="Video consultations available"
        />
      </div>

      <Field
        label="Short bio"
        htmlFor="bio"
        hint="A couple of sentences patients will read on your profile."
      >
        <Textarea
          id="bio"
          rows={4}
          value={draft.bio}
          onChange={(event) => update({ bio: event.target.value })}
          placeholder="I focus on preventive cardiology and long-term care planning…"
        />
      </Field>
    </div>
  );
}

const ESTABLISHMENT_ROLES: { value: EstablishmentRole; title: string; proof: string }[] = [
  {
    value: "owner",
    title: "I own this establishment",
    proof: "Ownership document or registration certificate",
  },
  { value: "rented", title: "It is rented by me", proof: "Rent agreement" },
  {
    value: "consultant",
    title: "I am a visiting consultant",
    proof: "Consultancy letter from the establishment",
  },
  { value: "home", title: "I consult from home", proof: "Utility bill or address proof" },
];

export function IdentityProofStep() {
  const { draft, update, errors } = useOnboarding();
  return (
    <div className="space-y-4">
      <Hint>
        Accepted: government photo ID (passport, driving licence, national ID). Documents stay on
        this device until the backend exposes an upload endpoint.
      </Hint>
      <DocumentUpload
        label="Upload identity proof"
        document={draft.identityProof}
        onChange={(identityProof) => update({ identityProof })}
        error={errors.identityProof}
      />
    </div>
  );
}

export function RegistrationProofStep() {
  const { draft, update, errors } = useOnboarding();
  return (
    <div className="space-y-4">
      <Hint>
        Accepted: medical registration certificate issued by your council, or a screenshot of your
        entry in the council register.
      </Hint>
      <DocumentUpload
        label="Upload registration proof"
        document={draft.registrationProof}
        onChange={(registrationProof) => update({ registrationProof })}
        error={errors.registrationProof}
      />
    </div>
  );
}

export function EstablishmentProofStep() {
  const { draft, update, errors } = useOnboarding();
  const selected = ESTABLISHMENT_ROLES.find((role) => role.value === draft.establishmentRole);

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label>
          What applies to you?<span className="ml-0.5 text-destructive">*</span>
        </Label>
        <RadioGroup
          value={draft.establishmentRole}
          onValueChange={(establishmentRole) =>
            update({
              establishmentRole: establishmentRole as EstablishmentRole,
              establishmentProof: null,
            })
          }
          className="grid gap-3"
        >
          {ESTABLISHMENT_ROLES.map((role) => (
            <Label
              key={role.value}
              htmlFor={`role-${role.value}`}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-xl border border-border p-4 transition-colors hover:bg-muted/60",
                draft.establishmentRole === role.value && "border-primary bg-primary/5",
              )}
            >
              <RadioGroupItem id={`role-${role.value}`} value={role.value} className="mt-1" />
              <span>
                <span className="block text-sm font-medium">{role.title}</span>
                <span className="block text-xs text-muted-foreground">
                  Proof needed: {role.proof}
                </span>
              </span>
            </Label>
          ))}
        </RadioGroup>
        <FieldError message={errors.establishmentRole} />
      </div>

      {selected && (
        <DocumentUpload
          label={`Upload ${selected.proof.toLowerCase()}`}
          document={draft.establishmentProof}
          onChange={(establishmentProof) => update({ establishmentProof })}
          error={errors.establishmentProof}
        />
      )}
    </div>
  );
}
